// app/frontend/pages/Admin/Dashboard.tsx
import { Grid, Paper, Group, Stack, Box, Badge, Table } from "@mantine/core";
import {
  IconVideo,
  IconUsers,
  IconCategory,
  IconRoute,
  IconNews,
  IconArticle,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";
import { Link } from "@inertiajs/react";
import { L2KH2, L2KH4, L2KH5, L2KText, L2KCaption } from "../../components/ui/Typography";
import AdminLayout from "../../components/layouts/AdminLayout";
import { SEO } from "../../components/common/SEO";

interface VideoSummary {
  id: number;
  slug: string;
  title: string;
  published: boolean;
  skill_level: string;
  created_at: string;
}

interface UserSummary {
  id: number;
  email: string;
  admin: boolean;
  created_at: string;
}

interface PostSummary {
  id: number;
  slug: string;
  title: string;
  category: string | null;
  post_type: string;
  created_at: string;
}

interface Props {
  stats: {
    videos: {
      total: number;
      published: number;
      drafts: number;
    };
    users: {
      total: number;
      admins: number;
      recent: number;
    };
    categories: {
      total: number;
    };
    learning_tracks: {
      total: number;
    };
    changelog: {
      updates: number;
      roadmap: number;
    };
    posts: {
      total: number;
      published: number;
      drafts: number;
    };
  };
  recent_videos: VideoSummary[];
  recent_users: UserSummary[];
  draft_videos: VideoSummary[];
  draft_posts: PostSummary[];
}

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  href?: string;
}

function StatCard({ title, value, subtitle, icon, color, href }: StatCardProps) {
  const content = (
    <Paper
      p="lg"
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        cursor: href ? "pointer" : "default",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        if (href) {
          e.currentTarget.style.borderColor = color;
          e.currentTarget.style.transform = "translateY(-2px)";
        }
      }}
      onMouseLeave={(e) => {
        if (href) {
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
          e.currentTarget.style.transform = "translateY(0)";
        }
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Box>
          <L2KCaption variant="tertiary" tt="uppercase" fw={600}>
            {title}
          </L2KCaption>
          <L2KH2 mb={0} style={{ color }}>
            {value}
          </L2KH2>
          {subtitle && (
            <L2KCaption variant="tertiary" mt="xs">
              {subtitle}
            </L2KCaption>
          )}
        </Box>
        <Box
          style={{
            backgroundColor: `${color}20`,
            padding: "12px",
            borderRadius: "8px",
            color,
          }}
        >
          {icon}
        </Box>
      </Group>
    </Paper>
  );

  return href ? <Link href={href}>{content}</Link> : content;
}

export default function Dashboard({
  stats,
  recent_videos,
  recent_users,
  draft_videos,
  draft_posts,
}: Props) {
  return (
    <>
      <SEO title="Admin Dashboard - Learntokiz" />
      <Stack gap="xl">
        <Box>
          <L2KH2 mb="xs">Dashboard</L2KH2>
          <L2KText variant="secondary">
            Overview of your Learntokiz platform
          </L2KText>
        </Box>

        {/* Stats Grid */}
        <Grid>
          <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
            <StatCard
              title="Videos"
              value={stats.videos.total}
              subtitle={`${stats.videos.published} published, ${stats.videos.drafts} drafts`}
              icon={<IconVideo size={24} />}
              color="#FF9800"
              href="/admin/videos"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
            <StatCard
              title="Users"
              value={stats.users.total}
              subtitle={`${stats.users.recent} joined this week`}
              icon={<IconUsers size={24} />}
              color="#4CAF50"
              href="/admin/users"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
            <StatCard
              title="Categories"
              value={stats.categories.total}
              icon={<IconCategory size={24} />}
              color="#2196F3"
              href="/admin/categories"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
            <StatCard
              title="Learning Tracks"
              value={stats.learning_tracks.total}
              icon={<IconRoute size={24} />}
              color="#9C27B0"
              href="/admin/learning_tracks"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
            <StatCard
              title="Changelog Updates"
              value={stats.changelog.updates}
              subtitle={`${stats.changelog.roadmap} roadmap items`}
              icon={<IconNews size={24} />}
              color="#E91E63"
              href="/admin/changelog_entries"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6, md: 4 }}>
            <StatCard
              title="Posts"
              value={stats.posts.total}
              subtitle={`${stats.posts.published} published, ${stats.posts.drafts} drafts`}
              icon={<IconArticle size={24} />}
              color="#00BCD4"
              href="/admin/posts"
            />
          </Grid.Col>
        </Grid>

        {/* Recent Activity */}
        <Grid>
          {/* Draft Videos */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper
              p="lg"
              radius="md"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Group justify="space-between" mb="md">
                <L2KH5 mb={0}>Draft Videos</L2KH5>
                <Badge color="orange" variant="light">
                  {stats.videos.drafts} pending
                </Badge>
              </Group>
              {draft_videos.length === 0 ? (
                <L2KText variant="tertiary" ta="center" py="md">
                  No draft videos
                </L2KText>
              ) : (
                <Table
                  horizontalSpacing="sm"
                  verticalSpacing="xs"
                  styles={{
                    table: { backgroundColor: "transparent" },
                    th: { color: "#888", borderColor: "rgba(255,255,255,0.1)" },
                    td: { borderColor: "rgba(255,255,255,0.05)" },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Title</Table.Th>
                      <Table.Th>Level</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {draft_videos.map((video) => (
                      <Table.Tr key={video.id}>
                        <Table.Td>
                          <Link
                            href={`/admin/videos/${video.id}/edit`}
                            style={{ color: "#D0D0D0", textDecoration: "none" }}
                          >
                            <L2KText size="sm" lineClamp={1}>
                              {video.title}
                            </L2KText>
                          </Link>
                        </Table.Td>
                        <Table.Td>
                          {video.skill_level ? (
                            <Badge size="xs" color="gray">
                              {video.skill_level}
                            </Badge>
                          ) : (
                            <Badge size="xs" color="red" variant="light">
                              Missing
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              <Link href="/admin/videos?status=draft">
                <L2KCaption
                  variant="accent"
                  mt="md"
                  style={{ display: "block", textAlign: "center" }}
                >
                  View all drafts →
                </L2KCaption>
              </Link>
            </Paper>
          </Grid.Col>

          {/* Recent Users */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper
              p="lg"
              radius="md"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Group justify="space-between" mb="md">
                <L2KH5 mb={0}>Recent Users</L2KH5>
                <Badge color="green" variant="light">
                  {stats.users.recent} this week
                </Badge>
              </Group>
              {recent_users.length === 0 ? (
                <L2KText variant="tertiary" ta="center" py="md">
                  No users yet
                </L2KText>
              ) : (
                <Table
                  horizontalSpacing="sm"
                  verticalSpacing="xs"
                  styles={{
                    table: { backgroundColor: "transparent" },
                    th: { color: "#888", borderColor: "rgba(255,255,255,0.1)" },
                    td: { borderColor: "rgba(255,255,255,0.05)" },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Joined</Table.Th>
                      <Table.Th>Role</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {recent_users.map((user) => (
                      <Table.Tr key={user.id}>
                        <Table.Td>
                          <L2KText size="sm" lineClamp={1}>
                            {user.email}
                          </L2KText>
                        </Table.Td>
                        <Table.Td>
                          <L2KCaption variant="tertiary">
                            {user.created_at}
                          </L2KCaption>
                        </Table.Td>
                        <Table.Td>
                          {user.admin ? (
                            <Badge size="xs" color="orange">
                              Admin
                            </Badge>
                          ) : (
                            <Badge size="xs" color="gray" variant="light">
                              Member
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              <Link href="/admin/users">
                <L2KCaption
                  variant="accent"
                  mt="md"
                  style={{ display: "block", textAlign: "center" }}
                >
                  View all users →
                </L2KCaption>
              </Link>
            </Paper>
          </Grid.Col>

          {/* Draft Posts */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper
              p="lg"
              radius="md"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Group justify="space-between" mb="md">
                <L2KH5 mb={0}>Draft Posts</L2KH5>
                <Badge color="cyan" variant="light">
                  {stats.posts.drafts} pending
                </Badge>
              </Group>
              {draft_posts.length === 0 ? (
                <L2KText variant="tertiary" ta="center" py="md">
                  No draft posts
                </L2KText>
              ) : (
                <Table
                  horizontalSpacing="sm"
                  verticalSpacing="xs"
                  styles={{
                    table: { backgroundColor: "transparent" },
                    th: { color: "#888", borderColor: "rgba(255,255,255,0.1)" },
                    td: { borderColor: "rgba(255,255,255,0.05)" },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Title</Table.Th>
                      <Table.Th>Category</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {draft_posts.map((post) => (
                      <Table.Tr key={post.id}>
                        <Table.Td>
                          <Link
                            href={`/admin/posts/${post.id}/edit`}
                            style={{ color: "#D0D0D0", textDecoration: "none" }}
                          >
                            <L2KText size="sm" lineClamp={1}>
                              {post.title}
                            </L2KText>
                          </Link>
                        </Table.Td>
                        <Table.Td>
                          {post.category ? (
                            <Badge size="xs" color="gray">
                              {post.category}
                            </Badge>
                          ) : (
                            <Badge size="xs" color="gray" variant="light">
                              —
                            </Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              <Link href="/admin/posts">
                <L2KCaption
                  variant="accent"
                  mt="md"
                  style={{ display: "block", textAlign: "center" }}
                >
                  View all posts →
                </L2KCaption>
              </Link>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
}

Dashboard.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
